import { AbstractModel } from '../base'
import { type ISite, type IClient, Client, Site } from '../client'
import { Employee, type IEmployee } from '../employee'
import { ChitOptionConstraintType, ChitState } from './chit.constants'
import type {
  IChit,
  IChitComment,
  IChitCommentData,
  IChitData,
  IChitDetail,
  IChitDetailData,
  IChitOption,
  IChitOptionAnswer,
  IChitOptionAnswerData,
  IChitOptionConstraint,
  IChitOptionConstraintData,
  IChitOptionConstraintField,
  IChitOptionConstraintFieldData,
  IChitOptionData
} from './chit.types'

/**
 * A class representing a Chit (Core chit information)
 */
export class Chit extends AbstractModel implements IChit {
  readonly chit_number?: number
  readonly state: number = ChitState.Assigned
  readonly chit_types: (IChitOption | number)[]
  readonly site: ISite | number
  readonly client: IClient | number
  readonly guards: (IEmployee | number)[] = []
  readonly created?: Date
  readonly modified?: Date
  readonly approver?: string
  readonly created_by?: string
  readonly modified_by?: string
  readonly chit_detail: IChitDetail

  constructor(data: IChitData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitData)[] = [
      'client',
      'site',
      'chit_types',
      'guards',
      'chit_detail'
    ]
    this.validate(data, requiredFields)

    // Initialize object assuming data valid
    if (data.chit_number) {
      this.chit_number = data.chit_number
    }

    if (data.state) {
      this.state = data.state
    }

    this.chit_types = data.chit_types!.map((type) =>
      typeof type === 'number' ? type : new ChitOption(type)
    )

    this.site = typeof data.site == 'number' ? data.site : new Site(data.site!)
    this.client = typeof data.client == 'number' ? data.client : new Client(data.client!)

    if (data.approver) {
      this.approver = data.approver
    }

    if (data.guards) {
      this.guards = data.guards.map((guard) =>
        typeof guard === 'number' ? guard : new Employee(guard)
      )
    }

    if (data.created) {
      this.created = typeof data.created === 'string' ? new Date(data.created) : data.created
    }

    if (data.modified) {
      this.modified = typeof data.modified === 'string' ? new Date(data.modified) : data.modified
    }

    if (data.created_by) {
      this.created_by = data.created_by
    }

    if (data.modified_by) {
      this.modified_by = data.modified_by
    }

    this.chit_detail = new ChitDetail(data.chit_detail!)
  }

  toString(): string {
    const siteText = typeof this.site === 'object' ? this.site.toString() : `Site ${this.site}`
    return `(${this.chit_number}) - ${siteText}`
  }

  canSubmit(): boolean {
    return this.state === ChitState.Assigned
  }

  canApprove(): boolean {
    return this.state === ChitState.Submitted
  }
}

/**
 * A class representing ChitDetail (Detailed chit information)
 */
export class ChitDetail extends AbstractModel implements IChitDetail {
  readonly ref_number: string = ''
  readonly conditions: IChitOptionAnswer[] = []
  readonly observations: IChitOptionAnswer[] = []
  readonly received_at: Date | null = null
  readonly departed_at: Date | null = null
  readonly arrived_at: Date | null = null
  readonly call_type: string = ''
  readonly call_name: string = ''
  readonly unit_number: string = ''
  readonly wo_number: string = ''
  readonly by: string = ''
  readonly tech_phone_number: string = ''
  readonly tech_name: string = ''
  readonly comments: string = ''

  constructor(data: IChitDetailData) {
    super()

    if (data.ref_number) {
      this.ref_number = data.ref_number
    }

    if (data.conditions) {
      this.conditions = data.conditions.map((condition) => new ChitOptionAnswer(condition))
    }

    if (data.observations) {
      this.observations = data.observations.map((observation) => new ChitOptionAnswer(observation))
    }

    if (data.departed_at) {
      this.departed_at =
        typeof data.departed_at === 'string' ? new Date(data.departed_at) : data.departed_at
    }

    if (data.arrived_at) {
      this.arrived_at =
        typeof data.arrived_at === 'string' ? new Date(data.arrived_at) : data.arrived_at
    }

    if (data.received_at) {
      this.received_at =
        typeof data.received_at === 'string' ? new Date(data.received_at) : data.received_at
    }

    if (data.call_type) {
      this.call_type = data.call_type
    }

    if (data.call_name) {
      this.call_name = data.call_name
    }

    if (data.unit_number) {
      this.unit_number = data.unit_number
    }

    if (data.wo_number) {
      this.wo_number = data.wo_number
    }

    if (data.by) {
      this.by = data.by
    }

    if (data.tech_phone_number) {
      this.tech_phone_number = data.tech_phone_number
    }

    if (data.tech_name) {
      this.tech_name = data.tech_name
    }

    if (data.comments) {
      this.comments = data.comments
    }
  }

  toString(): string {
    return `ChitDetail for Chit`
  }

  // Static helper methods for field constraints
  static isFieldHidden(chit: IChitData, field: keyof IChitDetailData): boolean {
    let hidden = false
    for (const option of chit.chit_types || []) {
      if (typeof option === 'object') {
        for (const constraint of option.constraints!) {
          if (
            constraint.fields!.some(
              (f) => f.value === field && constraint.type === ChitOptionConstraintType.Hidden
            )
          ) {
            hidden = true
          }
        }
      }
    }
    return hidden
  }

  static isFieldRequired(chit: IChitData, field: keyof IChitDetailData): boolean {
    let required = false
    for (const option of chit.chit_types || []) {
      if (typeof option === 'object') {
        for (const constraint of option.constraints!) {
          if (
            constraint.fields!.some(
              (f) => f.value === field && constraint.type === ChitOptionConstraintType.Required
            )
          ) {
            required = true
          }
        }
      }
    }
    return required
  }
}

export class ChitOptionAnswer extends AbstractModel implements IChitOptionAnswer {
  readonly id?: number
  readonly option: IChitOption | number
  readonly answer: boolean | null = null

  constructor(data: IChitOptionAnswerData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitOptionAnswerData)[] = ['option']
    this.validate(data, requiredFields)

    if (data.id) {
      this.id = data.id
    }

    this.option = typeof data.option == 'number' ? data.option : new ChitOption(data.option!)

    if (typeof data.answer !== 'undefined') {
      this.answer = data.answer
    }
  }

  toString(): string {
    let answerText = 'N/A'
    if (typeof this.answer === 'boolean') {
      answerText = this.answer ? 'Yes' : 'No'
    }
    const optionText =
      typeof this.option === 'object' ? this.option.toString() : `Option ${this.option}`
    return `${optionText}: ${answerText}`
  }
}

export class ChitOption extends AbstractModel implements IChitOption {
  readonly id?: number
  readonly text: string
  readonly type: string
  readonly constraints: IChitOptionConstraint[] = []

  constructor(data: IChitOptionData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitOptionData)[] = ['text', 'type']
    this.validate(data, requiredFields)

    if (data.id) {
      this.id = data.id
    }

    this.text = data.text!
    this.type = data.type!

    if (data.constraints) {
      this.constraints = data.constraints.map((constraint) => new ChitOptionConstraint(constraint))
    }
  }

  toString(): string {
    return this.text
  }
}

export class ChitOptionConstraint extends AbstractModel implements IChitOptionConstraint {
  readonly id?: number
  readonly fields: IChitOptionConstraintField[]
  readonly type: number

  constructor(data: IChitOptionConstraintData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitOptionConstraintData)[] = ['fields', 'type']
    this.validate(data, requiredFields)

    if (data.id) {
      this.id = data.id
    }

    this.fields = data.fields!.map((field) => new ChitOptionConstraintField(field))
    this.type = data.type!
  }

  toString(): string {
    return `Constraint ${this.id} (${this.type})`
  }
}

export class ChitOptionConstraintField extends AbstractModel implements IChitOptionConstraintField {
  readonly id?: number
  readonly text: string
  readonly value: string

  constructor(data: IChitOptionConstraintFieldData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitOptionConstraintFieldData)[] = ['text', 'value']
    this.validate(data, requiredFields)

    if (data.id) {
      this.id = data.id
    }

    this.text = data.text!
    this.value = data.value!
  }

  toString(): string {
    return `${this.text}: ${this.value}`
  }
}

export class ChitComment extends AbstractModel implements IChitComment {
  readonly id?: number
  readonly text: string
  readonly owner?: string
  readonly chit?: number
  readonly created_at?: Date
  readonly updated_at?: Date

  constructor(data: IChitCommentData) {
    super()

    // Validate data
    const requiredFields: (keyof IChitCommentData)[] = ['text']
    this.validate(data, requiredFields)

    if (data.id) {
      this.id = data.id
    }

    if (data.owner) {
      this.owner = data.owner
    }

    this.text = data.text

    if (data.created_at) {
      this.created_at =
        typeof data.created_at === 'string' ? new Date(data.created_at) : data.created_at
    }

    if (data.updated_at) {
      this.updated_at =
        typeof data.updated_at === 'string' ? new Date(data.updated_at) : data.updated_at
    }
  }

  toString(): string {
    return `${this.owner || 'Unknown'}: ${this.text}`
  }

  get isCommentEdited(): boolean {
    // Check if the comment has been edited by comparing timestamps
    if (!this.created_at || !this.updated_at) {
      return false
    }

    // Compare if updated_at is later than created_at
    return this.updated_at.getTime() > this.created_at.getTime()
  }
}
